import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  CheckCircle,
  TrendingUp,
  Shield,
  Lightbulb,
  Heart,
  ArrowRight,
} from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: Award,
      title: 'Expertise',
      description: 'Deep technical knowledge and industry experience',
    },
    {
      icon: CheckCircle,
      title: 'Reliable',
      description: 'Consistent delivery and dependable partnerships',
    },
    {
      icon: TrendingUp,
      title: 'Agile',
      description: 'Flexible and adaptive to changing requirements',
    },
    {
      icon: Shield,
      title: 'Scalable',
      description: 'Solutions that grow with your business',
    },
    {
      icon: Lightbulb,
      title: 'Transparency',
      description: 'Clear communication and honest practices',
    },
    {
      icon: Heart,
      title: 'Commitment',
      description: 'Dedicated to your success and satisfaction',
    },
  ];

  const hiringSteps = [
    {
      number: '01',
      title: 'Consultation',
      description: 'Understand your technical needs, budget, and timeline',
    },
    {
      number: '02',
      title: 'Sourcing',
      description: 'Tap into our talent network & recruitment platforms',
    },
    {
      number: '03',
      title: 'Screening',
      description: 'Technical assessments, interviews, background checks',
    },
    {
      number: '04',
      title: 'Onboarding',
      description: 'We manage documentation, compliance & smooth integration',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Us"
        description="Learn about OneSource IT Solutions - a specialized IT staffing and consulting firm dedicated to connecting businesses with top-tier technology talent in Hyderabad and across India."
        keywords="about OneSource, IT staffing company, Hyderabad IT recruitment, staffing agency"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Building the future of{' '}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 bg-clip-text text-transparent">
                IT staffing
              </span>
            </h1>
            <p className="text-xl text-slate-600">
              Learn more about our mission, values, and commitment to excellence in IT staffing
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Who we are
              </h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  We are a specialized IT staffing and consulting firm dedicated to connecting
                  businesses with top-tier technology talent. With a focus on quality, speed, and
                  industry alignment, we help organizations scale their teams efficiently.
                </p>
                <p>
                  Our mission is to simplify hiring and deliver workforce solutions that drive
                  innovation and growth. We understand that the right talent can transform your
                  business, and we're committed to making that connection seamless.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=800&q=80"
                alt="Team meeting"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Our mission
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            To empower businesses with exceptional IT talent that drives innovation, growth, and
            competitive advantage. We strive to be the trusted partner for organizations seeking to
            build high-performing technology teams through our human-centric approach, deep industry
            expertise, and unwavering commitment to quality.
          </p>
        </div>
      </section>

      {/* 4-Step Hiring Process */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our hiring process
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A streamlined approach to finding and onboarding the right talent for your team
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {hiringSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300">
                  <div className="text-5xl font-bold text-[#f9a825]/20 mb-4">{step.number}</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our values
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              These core values guide everything we do and define how we serve our clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-slate-200"
                >
                  <Icon className="w-10 h-10 text-[#f9a825] mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionsRef.current[4] = el)}
        className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-0 transition-opacity duration-1000 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-orange-500/10"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to build your dream team?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss how we can help you find the perfect IT talent for your organization
          </p>
          <Link
            to="/contact-us"
            className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
          >
            <span>Contact Us Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
